/**
 * Dots & Boxes Game Engine
 *
 * Core game logic: board setup, line placement, box completion detection,
 * extra turn on box completion, turn management, game-over detection, scoring.
 */

import {
  PlayerColor,
  PLAYER_COLORS,
  Line,
  DotsBoxesBoard,
  DotsBoxesMove,
  DotsBoxesGameState,
  DotsBoxesGameSettings,
  GridSize,
} from './types';

export class DotsBoxesEngine {
  private state: DotsBoxesGameState;

  constructor() {
    this.state = this.createEmptyState(5);
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize a new game with the given settings.
   */
  initializeGame(settings: DotsBoxesGameSettings): void {
    const gridSize = settings.gridSize;
    const boxCount = (gridSize - 1) * (gridSize - 1);
    const players = PLAYER_COLORS.slice(0, settings.playerCount);

    // Total lines: horizontal + vertical
    // Horizontal: gridSize rows × (gridSize-1) cols
    // Vertical: (gridSize-1) rows × gridSize cols
    const totalLines = gridSize * (gridSize - 1) + (gridSize - 1) * gridSize;

    const board = this.createEmptyBoard(gridSize);

    const scores: Record<PlayerColor, number> = {} as Record<PlayerColor, number>;
    for (const color of PLAYER_COLORS) {
      scores[color] = 0;
    }

    this.state = {
      board,
      gridSize,
      currentPlayerIndex: 0,
      players,
      moveHistory: [],
      gameStatus: 'active',
      scores,
      gameId: `dots-boxes-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
      totalLines,
      drawnLines: 0,
    };
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  /**
   * Draw a line on the board.
   * Returns the number of boxes completed by this move.
   * If boxes are completed, the current player gets another turn.
   */
  drawLine(line: Line): number {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    if (!this.isValidLine(line)) {
      throw new Error('Invalid line position');
    }

    if (this.isLineDrawn(line)) {
      throw new Error('Line already drawn');
    }

    const currentPlayer = this.getCurrentPlayer();
    const lineKey = this.getLineKey(line);

    // Draw the line
    if (line.direction === 'horizontal') {
      this.state.board.horizontalLines[line.row][line.col] = true;
    } else {
      this.state.board.verticalLines[line.row][line.col] = true;
    }
    this.state.board.lineOwners[lineKey] = currentPlayer;
    this.state.drawnLines++;

    // Check for completed boxes
    const completedBoxes = this.checkCompletedBoxes(line, currentPlayer);

    // Record the move
    const move: DotsBoxesMove = {
      player: currentPlayer,
      line,
      boxesCompleted: completedBoxes,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(move);

    // Update scores
    this.state.scores[currentPlayer] += completedBoxes;

    // Check if game is over
    if (this.state.drawnLines === this.state.totalLines) {
      this.endGame();
    } else if (completedBoxes === 0) {
      // No box completed — advance to next player
      this.state.currentPlayerIndex =
        (this.state.currentPlayerIndex + 1) % this.state.players.length;
    }
    // If completedBoxes > 0, current player gets another turn (no index change)

    return completedBoxes;
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  /**
   * Get all valid (undrawn) lines.
   */
  getValidMoves(): Line[] {
    const moves: Line[] = [];
    const gs = this.state.gridSize;

    // Horizontal lines: gridSize rows, (gridSize-1) cols
    for (let row = 0; row < gs; row++) {
      for (let col = 0; col < gs - 1; col++) {
        if (!this.state.board.horizontalLines[row][col]) {
          moves.push({ row, col, direction: 'horizontal' });
        }
      }
    }

    // Vertical lines: (gridSize-1) rows, gridSize cols
    for (let row = 0; row < gs - 1; row++) {
      for (let col = 0; col < gs; col++) {
        if (!this.state.board.verticalLines[row][col]) {
          moves.push({ row, col, direction: 'vertical' });
        }
      }
    }

    return moves;
  }

  /**
   * Check if a line position is valid (within bounds).
   */
  isValidLine(line: Line): boolean {
    const gs = this.state.gridSize;
    if (line.direction === 'horizontal') {
      return line.row >= 0 && line.row < gs && line.col >= 0 && line.col < gs - 1;
    } else {
      return line.row >= 0 && line.row < gs - 1 && line.col >= 0 && line.col < gs;
    }
  }

  /**
   * Check if a line has already been drawn.
   */
  isLineDrawn(line: Line): boolean {
    if (line.direction === 'horizontal') {
      return this.state.board.horizontalLines[line.row]?.[line.col] ?? false;
    } else {
      return this.state.board.verticalLines[line.row]?.[line.col] ?? false;
    }
  }

  // =========================================================================
  // Game Over
  // =========================================================================

  isGameOver(): boolean {
    return this.state.gameStatus === 'completed';
  }

  getScores(): Record<PlayerColor, number> {
    return { ...this.state.scores };
  }

  getWinner(): PlayerColor | 'draw' | undefined {
    return this.state.winner;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getCurrentPlayer(): PlayerColor {
    return this.state.players[this.state.currentPlayerIndex];
  }

  getCurrentPlayerIndex(): number {
    return this.state.currentPlayerIndex;
  }

  getPlayers(): PlayerColor[] {
    return [...this.state.players];
  }

  getBoard(): DotsBoxesBoard {
    return this.cloneBoard(this.state.board);
  }

  getGridSize(): GridSize {
    return this.state.gridSize;
  }

  getGameState(): DotsBoxesGameState {
    return {
      ...this.state,
      board: this.cloneBoard(this.state.board),
      moveHistory: [...this.state.moveHistory],
      players: [...this.state.players],
      scores: { ...this.state.scores },
    };
  }

  getMoveHistory(): DotsBoxesMove[] {
    return [...this.state.moveHistory];
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): DotsBoxesGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);
      if (!parsed.board || !parsed.gridSize) {
        return { error: 'Invalid state: missing board or gridSize' };
      }
      if (!['active', 'completed'].includes(parsed.gameStatus)) {
        return { error: 'Invalid state: invalid gameStatus' };
      }
      return parsed as DotsBoxesGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: DotsBoxesGameState): void {
    this.state = {
      ...state,
      board: this.cloneBoard(state.board),
      moveHistory: [...state.moveHistory],
      players: [...state.players],
      scores: { ...state.scores },
    };
  }

  // =========================================================================
  // Static Helpers (for AI use)
  // =========================================================================

  /**
   * Count how many sides a box has drawn.
   */
  static countBoxSides(
    board: DotsBoxesBoard,
    boxRow: number,
    boxCol: number,
    gridSize: GridSize
  ): number {
    let count = 0;
    // Top: horizontal line at (boxRow, boxCol)
    if (board.horizontalLines[boxRow]?.[boxCol]) count++;
    // Bottom: horizontal line at (boxRow+1, boxCol)
    if (board.horizontalLines[boxRow + 1]?.[boxCol]) count++;
    // Left: vertical line at (boxRow, boxCol)
    if (board.verticalLines[boxRow]?.[boxCol]) count++;
    // Right: vertical line at (boxRow, boxCol+1)
    if (board.verticalLines[boxRow]?.[boxCol + 1]) count++;
    return count;
  }

  /**
   * Get the boxes adjacent to a line.
   */
  static getAdjacentBoxes(
    line: Line,
    gridSize: GridSize
  ): { row: number; col: number }[] {
    const boxes: { row: number; col: number }[] = [];
    const maxBox = gridSize - 2; // max box index

    if (line.direction === 'horizontal') {
      // Horizontal line at (row, col) is the top of box (row, col) and bottom of box (row-1, col)
      if (line.row > 0 && line.row - 1 <= maxBox && line.col <= maxBox) {
        boxes.push({ row: line.row - 1, col: line.col });
      }
      if (line.row <= maxBox && line.col <= maxBox) {
        boxes.push({ row: line.row, col: line.col });
      }
    } else {
      // Vertical line at (row, col) is the left of box (row, col) and right of box (row, col-1)
      if (line.col > 0 && line.col - 1 <= maxBox && line.row <= maxBox) {
        boxes.push({ row: line.row, col: line.col - 1 });
      }
      if (line.col <= maxBox && line.row <= maxBox) {
        boxes.push({ row: line.row, col: line.col });
      }
    }

    return boxes;
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private createEmptyBoard(gridSize: GridSize): DotsBoxesBoard {
    const horizontalLines: boolean[][] = [];
    for (let row = 0; row < gridSize; row++) {
      horizontalLines.push(new Array(gridSize - 1).fill(false));
    }

    const verticalLines: boolean[][] = [];
    for (let row = 0; row < gridSize - 1; row++) {
      verticalLines.push(new Array(gridSize).fill(false));
    }

    const boxes: (PlayerColor | null)[][] = [];
    for (let row = 0; row < gridSize - 1; row++) {
      boxes.push(new Array(gridSize - 1).fill(null));
    }

    return { horizontalLines, verticalLines, boxes, lineOwners: {} };
  }

  private createEmptyState(gridSize: GridSize): DotsBoxesGameState {
    const scores: Record<PlayerColor, number> = {} as Record<PlayerColor, number>;
    for (const color of PLAYER_COLORS) {
      scores[color] = 0;
    }

    return {
      board: this.createEmptyBoard(gridSize),
      gridSize,
      currentPlayerIndex: 0,
      players: ['blue', 'red'],
      moveHistory: [],
      gameStatus: 'active',
      scores,
      gameId: '',
      startedAt: 0,
      totalLines: 0,
      drawnLines: 0,
    };
  }

  private getLineKey(line: Line): string {
    return `${line.row},${line.col},${line.direction}`;
  }

  /**
   * Check if drawing a line completed any boxes.
   * Returns the number of boxes completed.
   */
  private checkCompletedBoxes(line: Line, player: PlayerColor): number {
    const adjacentBoxes = DotsBoxesEngine.getAdjacentBoxes(line, this.state.gridSize);
    let completed = 0;

    for (const box of adjacentBoxes) {
      if (this.state.board.boxes[box.row][box.col] !== null) continue;

      const sides = DotsBoxesEngine.countBoxSides(
        this.state.board,
        box.row,
        box.col,
        this.state.gridSize
      );

      if (sides === 4) {
        this.state.board.boxes[box.row][box.col] = player;
        completed++;
      }
    }

    return completed;
  }

  private endGame(): void {
    this.state.gameStatus = 'completed';

    // Find the winner(s)
    let maxScore = -1;
    let winners: PlayerColor[] = [];

    for (const player of this.state.players) {
      const score = this.state.scores[player];
      if (score > maxScore) {
        maxScore = score;
        winners = [player];
      } else if (score === maxScore) {
        winners.push(player);
      }
    }

    if (winners.length === 1) {
      this.state.winner = winners[0];
    } else {
      this.state.winner = 'draw';
    }
  }

  private cloneBoard(board: DotsBoxesBoard): DotsBoxesBoard {
    return {
      horizontalLines: board.horizontalLines.map(row => [...row]),
      verticalLines: board.verticalLines.map(row => [...row]),
      boxes: board.boxes.map(row => [...row]),
      lineOwners: { ...board.lineOwners },
    };
  }
}
